<template>
  <div class="md-trend">
    <div class="md-trend__tabs">
      <button
          v-for="m in metrics" :key="m.key"
          type="button"
          class="md-trend__tab"
          :class="{ 'is-active': metric === m.key }"
          @click="metric = m.key"
      >
        {{ m.label }}
      </button>
    </div>
    <apexchart width="100%" height="215" type="area" :options="chartOptions" :series="series"/>
  </div>
</template>

<script>
export default {
  name: 'MdTrendChart',
  props: {
    // MonetaryDashboardSeriesPoint[]: { date, deposits, withdrawals (negative), ... }
    points: { type: Array, default: () => [] },
    isDarkSkin: { type: Boolean, default: false },
  },
  data: () => ({ metric: 'net' }),
  computed: {
    metrics() {
      return [
        { key: 'net', label: this.$t('mobileDash.metric.net') },
        { key: 'deposits', label: this.$t('mobileDash.metric.deposits') },
        { key: 'withdrawals', label: this.$t('mobileDash.metric.withdrawals') },
      ];
    },
    activeColor() {
      if (this.metric === 'deposits') return '#10b981';
      if (this.metric === 'withdrawals') return '#ef4444';
      return '#7367f0';
    },
    values() {
      return (this.points || []).map(p => {
        const deposits = p.deposits || 0;
        const withdrawals = p.withdrawals || 0; // negative
        if (this.metric === 'deposits') return deposits;
        if (this.metric === 'withdrawals') return Math.abs(withdrawals);
        return deposits + withdrawals;
      }).map(v => Number(v.toFixed(2)));
    },
    series() {
      return [{ name: this.metrics.find(m => m.key === this.metric).label, data: this.values }];
    },
    chartOptions() {
      const labelColor = this.isDarkSkin ? '#b4b7bd' : '#8a919c';
      const points = this.points || [];
      return {
        chart: {
          type: 'area',
          background: 'transparent',
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: true, speed: 250, animateGradually: { enabled: false } },
          parentHeightOffset: 0,
          sparkline: { enabled: false },
        },
        theme: { mode: this.isDarkSkin ? 'dark' : 'light' },
        colors: [this.activeColor],
        stroke: { curve: 'smooth', width: 2 },
        dataLabels: { enabled: false },
        // 1-2 data points are invisible on an area chart without markers
        markers: { size: points.length <= 2 ? 5 : 0, hover: { size: 5 } },
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.03, stops: [0, 90, 100] },
        },
        grid: {
          borderColor: this.isDarkSkin ? '#3b4253' : '#eef0f4',
          strokeDashArray: 4,
          padding: { left: 4, right: 8 },
        },
        legend: { show: false },
        xaxis: {
          categories: points.map(p => p.date),
          type: 'datetime',
          labels: { style: { colors: labelColor, fontSize: '10px' }, datetimeUTC: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: { enabled: false },
        },
        yaxis: {
          labels: {
            style: { colors: labelColor, fontSize: '10px' },
            formatter: val => new Intl.NumberFormat('en', { notation: 'compact' }).format(val),
          },
          tickAmount: 4,
        },
        tooltip: {
          x: { format: 'dd MMM yyyy' },
          y: {
            formatter: val => new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val),
          },
        },
      };
    },
  },
};
</script>

<style scoped>
.md-trend {
  background: var(--md-card-bg, #fff);
  border: 1px solid var(--md-card-border, rgba(34, 41, 47, 0.08));
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(34, 41, 47, 0.06), 0 5px 12px rgba(34, 41, 47, 0.1);
  padding: 0.6rem 0.5rem 0;
}
.dark-layout .md-trend {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.28), 0 7px 16px rgba(0, 0, 0, 0.22);
}
.md-trend__tabs {
  display: inline-flex;
  gap: 2px;
  margin: 0 0.25rem 0.25rem;
  padding: 3px;
  background: rgba(138, 145, 156, 0.1);
  border-radius: 8px;
}
.md-trend__tab {
  border: none;
  background: transparent;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--md-muted, #8a919c);
  transition: background 0.15s ease, color 0.15s ease;
}
.md-trend__tab.is-active {
  background: var(--md-card-bg, #fff);
  color: var(--md-strong, #24292f);
  box-shadow: 0 1px 3px rgba(34, 41, 47, 0.12);
}

.dark-layout .md-trend {
  --md-card-bg: #283046;
  --md-card-border: #3b4253;
  --md-strong: #d0d2d6;
}
</style>
